import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import {
  Loader2,
  Save,
  Plus,
  Trash2,
  Clock,
  Calendar as CalendarIcon,
  Bell,
  ShieldCheck,
  Hourglass,
  Settings as SettingsIcon,
  X,
  ChevronLeft,
  ChevronRight,
  CalendarX,
  AlertTriangle,
  RefreshCw,
  Phone,
  MessageCircle,
} from "lucide-react";
import apiClient from "@/lib/api-client";
import { useGlobalToast } from "../contexts/ToastContext";
import { useConfirm } from "../contexts/ConfirmContext";
import { usePermissions } from "../hooks/usePermissions";

interface Shift {
  startTime: string;
  endTime: string;
}
interface DaySchedule {
  isOpen: boolean;
  shifts: Shift[];
}
type WeeklySchedule = Record<string, DaySchedule>;

interface AppointmentSettings {
  id: string;
  weeklySchedule: WeeklySchedule;
  defaultDuration: number;
  slotInterval: number;
  bufferMinutes: number;
  minAdvanceHours: number;
  maxAdvanceDays: number;
  /** Tope de citas activas por cliente y semana (lunes–domingo). 0 = sin límite. */
  maxAppointmentsPerWeekPerCustomer: number;
  allowCancellation: boolean;
  cancellationMinHours: number;
  autoConfirm: boolean;
  reminderEmailEnabled: boolean;
  reminderEmailHoursBefore: number;
  reminderWhatsappEnabled: boolean;
  reminderWhatsappHoursBefore: number;
  timezone: string;
  clientChoosesSpecialist: boolean;
  clientChoosesRoom: boolean;
  rescheduleContactMethod: "phone" | "whatsapp";
  rescheduleWhatsappNumber: string | null;
}

interface Override {
  id: string;
  date: string;
  isClosed: boolean;
  reason: string | null;
  customShifts: Shift[] | null;
}

const DAYS: { key: string; short: string; long: string }[] = [
  { key: "1", short: "Lun", long: "Lunes" },
  { key: "2", short: "Mar", long: "Martes" },
  { key: "3", short: "Mié", long: "Miércoles" },
  { key: "4", short: "Jue", long: "Jueves" },
  { key: "5", short: "Vie", long: "Viernes" },
  { key: "6", short: "Sáb", long: "Sábado" },
  { key: "0", short: "Dom", long: "Domingo" },
];

function emptyShift(): Shift {
  return { startTime: "09:00", endTime: "14:00" };
}

function emptyBlockedRange(): Shift {
  return { startTime: "12:00", endTime: "14:00" };
}

function pad(n: number) {
  return String(n).padStart(2, "0");
}
function ymd(d: Date) {
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
}

/** Parsea una fecha "YYYY-MM-DD" como fecha LOCAL (evita el desfase de
 * `new Date("YYYY-MM-DD")`, que interpreta el string como UTC y puede caer
 * en el día anterior en zonas horarias con offset negativo). */
function parseLocalDate(dateStr: string): Date {
  const [y, m, d] = dateStr.split("-").map(Number);
  return new Date(y, (m || 1) - 1, d || 1);
}

function timeToMinutes(time: string): number {
  const [h, m] = time.split(":").map(Number);
  return (h || 0) * 60 + (m || 0);
}

function minutesToTime(minutes: number): string {
  const h = Math.floor(minutes / 60);
  const m = minutes % 60;
  return `${pad(h)}:${pad(m)}`;
}

/** Resta uno o varios intervalos (franjas a bloquear) de un conjunto de
 * turnos base. Devuelve los sub-intervalos resultantes (0, 1 o 2 por turno
 * base), en minutos convertidos de vuelta a "HH:MM". Ignora las franjas de
 * bloqueo inválidas (fin <= inicio). Ej.: base 10:00-20:00, bloqueo
 * 12:00-14:00 → [10:00-12:00, 14:00-20:00].
 */
function subtractIntervals(base: Shift[], blocked: Shift[]): Shift[] {
  const blockRanges = blocked
    .map((b) => ({ start: timeToMinutes(b.startTime), end: timeToMinutes(b.endTime) }))
    .filter((b) => b.end > b.start);

  let segments = base.map((s) => ({
    start: timeToMinutes(s.startTime),
    end: timeToMinutes(s.endTime),
  }));

  for (const block of blockRanges) {
    const next: { start: number; end: number }[] = [];
    for (const seg of segments) {
      if (block.end <= seg.start || block.start >= seg.end) {
        // Sin solape con este segmento
        next.push(seg);
        continue;
      }
      if (block.start > seg.start) {
        next.push({ start: seg.start, end: Math.min(block.start, seg.end) });
      }
      if (block.end < seg.end) {
        next.push({ start: Math.max(block.end, seg.start), end: seg.end });
      }
    }
    segments = next;
  }

  return segments
    .filter((seg) => seg.end > seg.start)
    .sort((a, b) => a.start - b.start)
    .map((seg) => ({
      startTime: minutesToTime(seg.start),
      endTime: minutesToTime(seg.end),
    }));
}

/** Rango razonable para pedir excepciones al backend (evita traer todo el
 * histórico): desde 1 mes antes del mes `center` hasta 13 meses después. */
function monthRange(center: Date): { from: string; to: string } {
  const from = new Date(center.getFullYear(), center.getMonth() - 1, 1);
  const to = new Date(center.getFullYear(), center.getMonth() + 14, 0);
  return { from: ymd(from), to: ymd(to) };
}

// Extrae un mensaje de error legible del cuerpo JSON (`error.response.data`
// de axios). Este módulo puede devolver `{ message }` (permisos, 401/404),
// `{ message, errors }` (zod .flatten(), validación de settings) o
// `{ message, details: [{ message }] }` en validaciones más específicas del
// backend; en ese último caso `details[0].message` es más útil que el
// genérico. Si no hay nada reconocible, se usa `fallback`.
function extractApiErrorMessage(err: unknown, fallback: string): string {
  const errData = (err as { response?: { data?: unknown } })?.response?.data;
  if (errData && typeof errData === "object") {
    const obj = errData as {
      message?: string;
      details?: { message?: string }[];
    };
    const firstDetail = Array.isArray(obj.details)
      ? obj.details.find((d) => typeof d?.message === "string" && d.message)
      : undefined;
    if (firstDetail?.message) return firstDetail.message;
    if (typeof obj.message === "string" && obj.message) return obj.message;
  }
  return fallback;
}

export default function AppointmentSettings() {
  const { showToast } = useGlobalToast();
  const { confirm } = useConfirm();
  const { can } = usePermissions();
  const canManage = can("servicios:editar");
  const [settings, setSettings] = useState<AppointmentSettings | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [loadError, setLoadError] = useState<{
    message: string;
    isBookingModeMismatch: boolean;
  } | null>(null);

  // Overrides
  const [overrides, setOverrides] = useState<Override[]>([]);
  const [overridesRange, setOverridesRange] = useState(() =>
    monthRange(new Date()),
  );
  const [calendarMonth, setCalendarMonth] = useState(() => {
    const d = new Date();
    d.setDate(1);
    return d;
  });
  const [overrideModal, setOverrideModal] = useState<{
    date: string;
    existing?: Override;
  } | null>(null);

  const load = async () => {
    setIsLoading(true);
    setLoadError(null);
    try {
      const [s, o] = await Promise.all([
        apiClient.get("/appointment-settings"),
        apiClient.get("/appointment-settings/overrides", {
          params: overridesRange,
        }),
      ]);
      setSettings(s.data.data);
      setOverrides(o.data.data ?? []);
    } catch (err) {
      const response = (
        err as { response?: { status?: number; data?: unknown } }
      )?.response;
      const status = response?.status;
      const data = response?.data as { code?: string; message?: string } | undefined;
      const message = data?.message ?? "";
      const isBookingModeMismatch =
        status === 403 &&
        (data?.code === "BOOKING_MODE_MISMATCH" ||
          message.includes("BOOKING_MODE_MISMATCH"));
      setLoadError({
        message: isBookingModeMismatch
          ? "Esta sección es solo para negocios que trabajan con citas."
          : message || "No se pudo cargar la configuración",
        isBookingModeMismatch,
      });
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /** Si el mes visitado del calendario cae fuera del rango de excepciones ya
   * cargado, amplía el rango y vuelve a pedir al backend (evita traer todo
   * el histórico de golpe, pero permite navegar meses lejanos). */
  const ensureOverridesRangeCovers = async (month: Date) => {
    const monthStart = ymd(new Date(month.getFullYear(), month.getMonth(), 1));
    const monthEnd = ymd(new Date(month.getFullYear(), month.getMonth() + 1, 0));
    if (monthStart >= overridesRange.from && monthEnd <= overridesRange.to) {
      return;
    }
    const newRange = monthRange(month);
    setOverridesRange(newRange);
    try {
      const o = await apiClient.get("/appointment-settings/overrides", {
        params: newRange,
      });
      setOverrides(o.data.data ?? []);
    } catch (err) {
      showToast(
        extractApiErrorMessage(err, "No se pudieron cargar las excepciones"),
        "error",
      );
    }
  };

  const updateField = <K extends keyof AppointmentSettings>(
    key: K,
    value: AppointmentSettings[K],
  ) => {
    if (!settings) return;
    setSettings({ ...settings, [key]: value });
  };

  const updateDay = (key: string, patch: Partial<DaySchedule>) => {
    if (!settings) return;
    const day = settings.weeklySchedule[key] ?? { isOpen: false, shifts: [] };
    setSettings({
      ...settings,
      weeklySchedule: {
        ...settings.weeklySchedule,
        [key]: { ...day, ...patch },
      },
    });
  };

  const addShift = (key: string) => {
    if (!settings) return;
    const day = settings.weeklySchedule[key] ?? { isOpen: true, shifts: [] };
    updateDay(key, { isOpen: true, shifts: [...day.shifts, emptyShift()] });
  };

  const removeShift = (key: string, idx: number) => {
    if (!settings) return;
    const day = settings.weeklySchedule[key];
    if (!day) return;
    const shifts = day.shifts.filter((_, i) => i !== idx);
    updateDay(key, { shifts, isOpen: shifts.length > 0 ? day.isOpen : false });
  };

  const updateShift = (key: string, idx: number, patch: Partial<Shift>) => {
    if (!settings) return;
    const day = settings.weeklySchedule[key];
    if (!day) return;
    const shifts = day.shifts.map((s, i) =>
      i === idx ? { ...s, ...patch } : s,
    );
    updateDay(key, { shifts });
  };

  const validate = (): string | null => {
    if (!settings) return null;
    for (const day of DAYS) {
      const d = settings.weeklySchedule[day.key];
      if (!d || !d.isOpen) continue;
      for (let i = 0; i < d.shifts.length; i++) {
        const s = d.shifts[i];
        if (s.startTime >= s.endTime) {
          return `${day.long}: el turno ${i + 1} tiene hora de fin anterior o igual a la de inicio.`;
        }
        for (let j = 0; j < i; j++) {
          const o = d.shifts[j];
          if (s.startTime < o.endTime && o.startTime < s.endTime) {
            return `${day.long}: los turnos ${j + 1} y ${i + 1} se solapan.`;
          }
        }
      }
    }
    if (settings.defaultDuration < 5 || settings.defaultDuration > 480)
      return "La duración por defecto debe estar entre 5 y 480 minutos.";
    if (settings.slotInterval < 5 || settings.slotInterval > 120)
      return "El intervalo entre slots debe estar entre 5 y 120 minutos.";
    if (settings.maxAdvanceDays < 1 || settings.maxAdvanceDays > 365)
      return "La antelación máxima debe estar entre 1 y 365 días.";
    if (settings.minAdvanceHours < 0 || settings.minAdvanceHours > 720)
      return "La antelación mínima debe estar entre 0 y 720 horas.";
    const weeklyLimit = settings.maxAppointmentsPerWeekPerCustomer ?? 0;
    if (!Number.isInteger(weeklyLimit) || weeklyLimit < 0 || weeklyLimit > 50)
      return "El máximo de citas por cliente y semana debe ser un número entero entre 0 y 50 (0 = sin límite).";
    if (
      settings.cancellationMinHours < 0 ||
      settings.cancellationMinHours > 720
    )
      return "La antelación mínima para cancelar debe estar entre 0 y 720 horas.";
    if (
      settings.reminderEmailHoursBefore < 0 ||
      settings.reminderEmailHoursBefore > 168
    )
      return "Las horas antes del recordatorio por email deben estar entre 0 y 168.";
    if (
      settings.reminderWhatsappHoursBefore < 0 ||
      settings.reminderWhatsappHoursBefore > 168
    )
      return "Las horas antes del recordatorio por WhatsApp deben estar entre 0 y 168.";
    return null;
  };

  const save = async () => {
    if (!settings) return;
    const err = validate();
    if (err) {
      showToast(err, "error");
      return;
    }
    setIsSaving(true);
    try {
      const payload = { ...settings };
      delete (payload as any).id;
      delete (payload as any).createdAt;
      delete (payload as any).updatedAt;
      delete (payload as any).organizationId;
      const res = await apiClient.put("/appointment-settings", payload);
      setSettings(res.data.data);
      showToast("Configuración guardada");
    } catch (err) {
      showToast(extractApiErrorMessage(err, "No se pudo guardar"), "error");
    } finally {
      setIsSaving(false);
    }
  };

  // Overrides helpers
  const monthDays = useMemo(() => {
    const first = new Date(calendarMonth);
    first.setDate(1);
    const startWeekDay = (first.getDay() + 6) % 7; // Lunes=0
    const lastDay = new Date(
      calendarMonth.getFullYear(),
      calendarMonth.getMonth() + 1,
      0,
    ).getDate();
    const days: (Date | null)[] = [];
    for (let i = 0; i < startWeekDay; i++) days.push(null);
    for (let d = 1; d <= lastDay; d++) {
      days.push(
        new Date(calendarMonth.getFullYear(), calendarMonth.getMonth(), d),
      );
    }
    while (days.length % 7 !== 0) days.push(null);
    return days;
  }, [calendarMonth]);

  const overridesByDate = useMemo(() => {
    const map = new Map<string, Override>();
    overrides.forEach((o) => map.set(o.date.slice(0, 10), o));
    return map;
  }, [overrides]);

  const openOverride = (date: Date) => {
    const dateStr = ymd(date);
    setOverrideModal({ date: dateStr, existing: overridesByDate.get(dateStr) });
  };

  const saveOverride = async (data: {
    isClosed: boolean;
    reason: string;
    customShifts: Shift[] | null;
  }) => {
    if (!overrideModal) return;
    try {
      const res = await apiClient.post("/appointment-settings/overrides", {
        date: overrideModal.date,
        ...data,
      });
      setOverrideModal(null);
      const o = await apiClient.get("/appointment-settings/overrides", {
        params: overridesRange,
      });
      setOverrides(o.data.data ?? []);
      const warnings: string[] = res.data?.warnings ?? [];
      if (warnings.length > 0) {
        showToast(warnings.join(" "), "info");
      } else {
        showToast("Excepción guardada");
      }
    } catch (err) {
      showToast(
        extractApiErrorMessage(err, "No se pudo guardar la excepción"),
        "error",
      );
    }
  };

  const deleteOverride = async (id: string) => {
    if (!(await confirm({ title: "¿Eliminar esta excepción?", message: "Se eliminará esta excepción de horario.", confirmText: "Sí, eliminar", danger: true }))) return;
    try {
      await apiClient.delete(`/appointment-settings/overrides/${id}`);
      setOverrides(overrides.filter((o) => o.id !== id));
      setOverrideModal(null);
      showToast("Excepción eliminada");
    } catch (err) {
      showToast(extractApiErrorMessage(err, "No se pudo eliminar"), "error");
    }
  };

  if (isLoading) {
    return (
      <div className="min-h-[60vh] flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-indigo-600" />
      </div>
    );
  }

  if (loadError) {
    return (
      <div className="min-h-[60vh] flex flex-col items-center justify-center gap-4 p-6 text-center">
        {loadError.isBookingModeMismatch ? (
          <>
            <CalendarX className="w-10 h-10 text-gray-400" />
            <p className="text-gray-600 max-w-sm">{loadError.message}</p>
            <Link
              to="/"
              className="inline-flex items-center gap-2 px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors text-sm font-medium"
            >
              Volver al panel
            </Link>
          </>
        ) : (
          <>
            <AlertTriangle className="w-10 h-10 text-red-500" />
            <p className="text-red-600 max-w-sm">{loadError.message}</p>
            <button
              onClick={load}
              className="inline-flex items-center gap-2 px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors text-sm font-medium"
            >
              <RefreshCw className="w-4 h-4" />
              Reintentar
            </button>
          </>
        )}
      </div>
    );
  }

  if (!settings) {
    return (
      <div className="min-h-[60vh] flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-indigo-600" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 p-3 md:p-6">
      {/* Header */}
      <div className="mb-4 md:mb-6 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div>
          <h1 className="text-xl md:text-2xl font-bold text-gray-900">
            Configuración de citas
          </h1>
          <p className="text-gray-500 text-sm mt-1">
            Horario del centro, reglas de reserva, cancelación y recordatorios.
          </p>
        </div>
        <button
          onClick={save}
          disabled={isSaving || !canManage}
          title={canManage ? undefined : "No tienes permiso para editar servicios"}
          className="inline-flex items-center gap-2 px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors text-sm font-medium disabled:opacity-50"
        >
          {isSaving ? (
            <Loader2 className="w-4 h-4 animate-spin" />
          ) : (
            <Save className="w-4 h-4" />
          )}
          Guardar cambios
        </button>
      </div>

      {!canManage && (
        <div className="mb-4 p-3 bg-amber-50 border border-amber-200 rounded-lg text-sm text-amber-800">
          Solo quien tenga permiso de servicios puede editar la configuración
          de citas. Puedes consultarla, pero no guardar cambios.
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 md:gap-6">
        {/* Columna principal (2/3) */}
        <div className="lg:col-span-2 space-y-4 md:space-y-6">
          {/* Horario del centro */}
          <Section
            icon={<Clock className="w-5 h-5" />}
            title="Horario del centro"
            description="Marca los días que abre el centro y los turnos de cada día. Los profesionales solo podrán trabajar dentro de este horario."
          >
            <div className="space-y-3">
              {DAYS.map((day) => {
                const d = settings.weeklySchedule[day.key] ?? {
                  isOpen: false,
                  shifts: [],
                };
                return (
                  <div
                    key={day.key}
                    className="border border-gray-200 rounded-lg p-3"
                  >
                    <div className="flex items-center justify-between gap-3 mb-2">
                      <label className="flex items-center gap-2 cursor-pointer">
                        <input
                          type="checkbox"
                          checked={d.isOpen}
                          disabled={!canManage}
                          onChange={(e) =>
                            updateDay(day.key, {
                              isOpen: e.target.checked,
                              shifts:
                                e.target.checked && d.shifts.length === 0
                                  ? [emptyShift()]
                                  : d.shifts,
                            })
                          }
                          className="w-4 h-4 text-indigo-600 disabled:opacity-50 disabled:cursor-not-allowed"
                        />
                        <span className="font-medium text-gray-900 w-20">
                          {day.long}
                        </span>
                        {!d.isOpen && (
                          <span className="text-xs text-gray-400">Cerrado</span>
                        )}
                      </label>
                      {d.isOpen && (
                        <button
                          onClick={() => addShift(day.key)}
                          disabled={!canManage}
                          className="text-indigo-600 hover:text-indigo-700 text-sm inline-flex items-center gap-1 disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                          <Plus className="w-4 h-4" /> Turno
                        </button>
                      )}
                    </div>
                    {d.isOpen && (
                      <div className="space-y-2 ml-6">
                        {d.shifts.length === 0 && (
                          <p className="text-xs text-gray-400">
                            Añade un turno (ej. 09:00–14:00)
                          </p>
                        )}
                        {d.shifts.map((s, idx) => (
                          <div
                            key={idx}
                            className="flex items-center gap-2 flex-wrap"
                          >
                            <input
                              type="time"
                              value={s.startTime}
                              disabled={!canManage}
                              onChange={(e) =>
                                updateShift(day.key, idx, {
                                  startTime: e.target.value,
                                })
                              }
                              className="px-2 py-1 border border-gray-300 rounded text-sm disabled:bg-gray-100 disabled:cursor-not-allowed"
                            />
                            <span className="text-gray-400">–</span>
                            <input
                              type="time"
                              value={s.endTime}
                              disabled={!canManage}
                              onChange={(e) =>
                                updateShift(day.key, idx, {
                                  endTime: e.target.value,
                                })
                              }
                              className="px-2 py-1 border border-gray-300 rounded text-sm disabled:bg-gray-100 disabled:cursor-not-allowed"
                            />
                            <button
                              onClick={() => removeShift(day.key, idx)}
                              disabled={!canManage}
                              className="ml-auto text-red-500 hover:text-red-700 disabled:opacity-50 disabled:cursor-not-allowed"
                              title="Eliminar turno"
                            >
                              <Trash2 className="w-4 h-4" />
                            </button>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </Section>

          {/* Excepciones / días especiales */}
          <Section
            icon={<CalendarX className="w-5 h-5" />}
            title="Días especiales y festivos"
            description="Cierra el centro días concretos o aplica horarios especiales. Pulsa cualquier día del calendario."
          >
            <div className="flex items-center justify-between mb-3">
              <button
                onClick={() => {
                  const d = new Date(calendarMonth);
                  d.setMonth(d.getMonth() - 1);
                  setCalendarMonth(d);
                  ensureOverridesRangeCovers(d);
                }}
                className="p-1 rounded hover:bg-gray-100"
              >
                <ChevronLeft className="w-4 h-4" />
              </button>
              <span className="font-medium capitalize">
                {calendarMonth.toLocaleDateString("es-ES", {
                  month: "long",
                  year: "numeric",
                })}
              </span>
              <button
                onClick={() => {
                  const d = new Date(calendarMonth);
                  d.setMonth(d.getMonth() + 1);
                  setCalendarMonth(d);
                  ensureOverridesRangeCovers(d);
                }}
                className="p-1 rounded hover:bg-gray-100"
              >
                <ChevronRight className="w-4 h-4" />
              </button>
            </div>
            <div className="grid grid-cols-7 gap-1 text-xs">
              {["Lun", "Mar", "Mié", "Jue", "Vie", "Sáb", "Dom"].map((d) => (
                <div
                  key={d}
                  className="text-center font-medium text-gray-500 py-1"
                >
                  {d}
                </div>
              ))}
              {monthDays.map((day, i) => {
                if (!day) return <div key={i} />;
                const dateStr = ymd(day);
                const ov = overridesByDate.get(dateStr);
                const isToday = ymd(new Date()) === dateStr;
                return (
                  <button
                    key={i}
                    onClick={() => openOverride(day)}
                    className={`
                      aspect-square flex flex-col items-center justify-center rounded-lg border text-xs transition-colors
                      ${
                        ov?.isClosed
                          ? "bg-red-50 border-red-300 text-red-700 hover:bg-red-100"
                          : ov
                            ? "bg-amber-50 border-amber-300 text-amber-700 hover:bg-amber-100"
                            : "bg-white border-gray-200 hover:bg-gray-50"
                      }
                      ${isToday ? "ring-2 ring-indigo-400" : ""}
                    `}
                  >
                    <span className="font-medium">{day.getDate()}</span>
                    {ov && (
                      <span className="text-[10px] mt-0.5 truncate max-w-full px-1">
                        {ov.isClosed ? "Cerrado" : "Especial"}
                      </span>
                    )}
                  </button>
                );
              })}
            </div>
            {overrides.length > 0 && (
              <div className="mt-4 pt-4 border-t">
                <h4 className="text-xs font-medium text-gray-500 uppercase mb-2">
                  Próximas excepciones
                </h4>
                <div className="space-y-1">
                  {overrides
                    .filter(
                      (o) =>
                        parseLocalDate(o.date.slice(0, 10)) >=
                        parseLocalDate(ymd(new Date())),
                    )
                    .slice(0, 5)
                    .map((o) => (
                      <div
                        key={o.id}
                        className="flex items-center justify-between text-sm"
                      >
                        <span>
                          {parseLocalDate(o.date.slice(0, 10)).toLocaleDateString(
                            "es-ES",
                            {
                              day: "numeric",
                              month: "short",
                            },
                          )}{" "}
                          —{" "}
                          <span
                            className={
                              o.isClosed ? "text-red-600" : "text-amber-600"
                            }
                          >
                            {o.isClosed ? "Cerrado" : "Horario especial"}
                          </span>
                          {o.reason ? ` · ${o.reason}` : ""}
                        </span>
                        <button
                          onClick={() => deleteOverride(o.id)}
                          disabled={!canManage}
                          className="text-red-500 hover:text-red-700 disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    ))}
                </div>
              </div>
            )}
          </Section>
        </div>

        {/* Columna lateral (1/3) */}
        <div className="space-y-4 md:space-y-6">
          {/* Reglas de reserva */}
          <Section
            icon={<SettingsIcon className="w-5 h-5" />}
            title="Reglas de reserva"
            description="Cómo se ofrecen las horas a los clientes."
          >
            <NumberField
              label="Duración por defecto (min)"
              hint="Valor por defecto. Cada servicio define su propia duración, que tiene prioridad sobre este."
              value={settings.defaultDuration}
              min={5}
              max={480}
              step={5}
              onChange={(v) => updateField("defaultDuration", v)}
              disabled={!canManage}
            />
            <NumberField
              label="Intervalo entre slots (min)"
              hint="Cada cuánto se ofrece una hora libre en el widget de citas (ej. cada 15 min)."
              value={settings.slotInterval}
              min={5}
              max={120}
              step={5}
              onChange={(v) => updateField("slotInterval", v)}
              disabled={!canManage}
            />
            {/* Buffer genérico oculto: el espaciado ahora lo marca el
                intervalo entre slots (con override por servicio). El campo
                sigue en `settings`/payload para no romper el backend, solo
                deja de ser editable desde esta UI. */}
            <NumberField
              label="Antelación mínima (horas)"
              hint="Tiempo mínimo antes de la cita para poder reservar."
              value={settings.minAdvanceHours}
              min={0}
              max={720}
              step={0.5}
              onChange={(v) => updateField("minAdvanceHours", v)}
              disabled={!canManage}
            />
            <NumberField
              label="Antelación máxima (días)"
              hint="Hasta cuántos días en el futuro pueden reservar."
              value={settings.maxAdvanceDays}
              min={1}
              max={365}
              step={1}
              onChange={(v) => updateField("maxAdvanceDays", v)}
              disabled={!canManage}
            />
            {/* Tope semanal por cliente (3-oct-2026): frena que un mismo
                cliente acumule varias citas en la misma semana desde el
                widget/app. 0 = sin límite. El personal no queda bloqueado:
                en Citas solo se le avisa al crear una por encima del tope. */}
            <NumberField
              label="Máximo de citas por cliente y semana"
              hint="Cuántas citas activas puede tener un mismo cliente en una semana (de lunes a domingo) al reservar online. 0 = sin límite. Las canceladas no cuentan. Desde el panel siempre puedes añadir más: solo se te avisa."
              value={settings.maxAppointmentsPerWeekPerCustomer ?? 0}
              min={0}
              max={50}
              step={1}
              onChange={(v) =>
                updateField(
                  "maxAppointmentsPerWeekPerCustomer",
                  Math.max(0, Math.round(v)),
                )
              }
              disabled={!canManage}
            />
            <div className="pt-2 border-t border-gray-100 space-y-3">
              <ToggleField
                label="El cliente puede elegir profesional"
                hint="En el widget de citas, el cliente verá un selector de profesional (con la opción 'Cualquiera disponible'). Si está apagado, el sistema asigna el profesional automáticamente."
                checked={settings.clientChoosesSpecialist}
                onChange={(v) => updateField("clientChoosesSpecialist", v)}
                disabled={!canManage}
              />
              <ToggleField
                label="El cliente puede elegir sala"
                hint="Próximamente disponible en el widget de citas."
                checked={settings.clientChoosesRoom}
                onChange={(v) => updateField("clientChoosesRoom", v)}
                disabled={!canManage}
              />
            </div>
          </Section>

          {/* Confirmación */}
          <Section
            icon={<ShieldCheck className="w-5 h-5" />}
            title="Confirmación"
          >
            <ToggleField
              label="Confirmar citas automáticamente"
              hint="Si está apagado, las citas quedan pendientes hasta que las apruebes."
              checked={settings.autoConfirm}
              onChange={(v) => updateField("autoConfirm", v)}
              disabled={!canManage}
            />
          </Section>

          {/* Cancelación */}
          <Section
            icon={<Hourglass className="w-5 h-5" />}
            title="Política de cancelación"
          >
            <ToggleField
              label="Permitir cancelar online"
              checked={settings.allowCancellation}
              onChange={(v) => updateField("allowCancellation", v)}
              disabled={!canManage}
            />
            {settings.allowCancellation && (
              <NumberField
                label="Antelación mínima para cancelar (horas)"
                hint="Pasado este tiempo, ya no podrán cancelar online."
                value={settings.cancellationMinHours}
                min={0}
                max={720}
                step={1}
                onChange={(v) => updateField("cancellationMinHours", v)}
                disabled={!canManage}
              />
            )}
          </Section>

          {/* Reagendar cita */}
          <Section
            icon={<Phone className="w-5 h-5" />}
            title="Reagendar cita"
            description="Cómo contacta el cliente para cambiar la fecha u hora de su cita. Se muestra en el email/WhatsApp de la cita y en la página pública de gestión."
          >
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
              <button
                type="button"
                onClick={() => updateField("rescheduleContactMethod", "phone")}
                disabled={!canManage}
                className={`py-2 px-3 rounded-lg border text-sm font-medium disabled:opacity-50 disabled:cursor-not-allowed inline-flex items-center justify-center gap-2 ${
                  settings.rescheduleContactMethod === "phone"
                    ? "bg-indigo-50 border-indigo-300 text-indigo-700"
                    : "border-gray-200 text-gray-600"
                }`}
              >
                <Phone className="w-4 h-4" />
                Llamar por teléfono
              </button>
              <button
                type="button"
                onClick={() => updateField("rescheduleContactMethod", "whatsapp")}
                disabled={!canManage}
                className={`py-2 px-3 rounded-lg border text-sm font-medium disabled:opacity-50 disabled:cursor-not-allowed inline-flex items-center justify-center gap-2 ${
                  settings.rescheduleContactMethod === "whatsapp"
                    ? "bg-emerald-50 border-emerald-300 text-emerald-700"
                    : "border-gray-200 text-gray-600"
                }`}
              >
                <MessageCircle className="w-4 h-4" />
                Escribir por WhatsApp
              </button>
            </div>
            {settings.rescheduleContactMethod === "whatsapp" && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Número de WhatsApp (opcional)
                </label>
                <input
                  type="text"
                  value={settings.rescheduleWhatsappNumber ?? ""}
                  disabled={!canManage}
                  onChange={(e) =>
                    updateField(
                      "rescheduleWhatsappNumber",
                      e.target.value === "" ? null : e.target.value,
                    )
                  }
                  placeholder="34 612 34 56 78"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm disabled:bg-gray-100 disabled:cursor-not-allowed"
                />
                <p className="text-xs text-gray-500 mt-1">
                  Si lo dejas vacío se usa el teléfono del negocio. Incluye el
                  prefijo del país (ej. 34 612 34 56 78).
                </p>
              </div>
            )}
          </Section>

          {/* Recordatorios */}
          <Section icon={<Bell className="w-5 h-5" />} title="Recordatorios">
            <ToggleField
              label="Recordatorio por email"
              checked={settings.reminderEmailEnabled}
              onChange={(v) => updateField("reminderEmailEnabled", v)}
              disabled={!canManage}
            />
            {settings.reminderEmailEnabled && (
              <NumberField
                label="Horas antes (email)"
                value={settings.reminderEmailHoursBefore}
                min={0}
                max={168}
                step={1}
                onChange={(v) => updateField("reminderEmailHoursBefore", v)}
                disabled={!canManage}
              />
            )}
            <ToggleField
              label="Recordatorio por WhatsApp"
              checked={settings.reminderWhatsappEnabled}
              onChange={(v) => updateField("reminderWhatsappEnabled", v)}
              disabled={!canManage}
            />
            {settings.reminderWhatsappEnabled && (
              <NumberField
                label="Horas antes (WhatsApp)"
                value={settings.reminderWhatsappHoursBefore}
                min={0}
                max={168}
                step={1}
                onChange={(v) => updateField("reminderWhatsappHoursBefore", v)}
                disabled={!canManage}
              />
            )}
          </Section>

          {/* Zona horaria */}
          <Section
            icon={<CalendarIcon className="w-5 h-5" />}
            title="Zona horaria"
          >
            <select
              value={settings.timezone}
              onChange={(e) => updateField("timezone", e.target.value)}
              disabled={!canManage}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm disabled:bg-gray-100 disabled:cursor-not-allowed"
            >
              <option value="Europe/Madrid">Europe/Madrid</option>
              <option value="Atlantic/Canary">Atlantic/Canary</option>
              <option value="Europe/Lisbon">Europe/Lisbon</option>
              <option value="Europe/Paris">Europe/Paris</option>
              <option value="Europe/London">Europe/London</option>
              <option value="America/Mexico_City">America/Mexico_City</option>
              <option value="America/Bogota">America/Bogota</option>
              <option value="America/Argentina/Buenos_Aires">
                America/Argentina/Buenos_Aires
              </option>
            </select>
          </Section>
        </div>
      </div>

      {overrideModal && (
        <OverrideModal
          dateStr={overrideModal.date}
          existing={overrideModal.existing}
          weeklySchedule={settings.weeklySchedule}
          canManage={canManage}
          onClose={() => setOverrideModal(null)}
          onSave={saveOverride}
          onDelete={
            overrideModal.existing
              ? () => deleteOverride(overrideModal.existing!.id)
              : undefined
          }
        />
      )}
    </div>
  );
}

function Section({
  icon,
  title,
  description,
  children,
}: {
  icon: React.ReactNode;
  title: string;
  description?: string;
  children: React.ReactNode;
}) {
  return (
    <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
      <div className="px-4 py-3 border-b border-gray-100 flex items-start gap-3">
        <div className="text-indigo-600 mt-0.5">{icon}</div>
        <div>
          <h3 className="font-semibold text-gray-900">{title}</h3>
          {description && (
            <p className="text-xs text-gray-500 mt-0.5">{description}</p>
          )}
        </div>
      </div>
      <div className="p-4 space-y-3">{children}</div>
    </div>
  );
}

function NumberField({
  label,
  hint,
  value,
  min,
  max,
  step,
  onChange,
  disabled,
}: {
  label: string;
  hint?: string;
  value: number;
  min?: number;
  max?: number;
  step?: number;
  onChange: (v: number) => void;
  disabled?: boolean;
}) {
  // Use internal string state to allow empty field while editing
  const [raw, setRaw] = useState(String(value));

  // Sync if parent value changes (e.g. after save/reset)
  useEffect(() => {
    setRaw(String(value));
  }, [value]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    // Permite dígitos y UN único punto decimal (los campos con step
    // fraccionario, ej. antelación mínima en horas, admiten "1.5").
    let cleaned = e.target.value.replace(/[^0-9.]/g, "");
    const firstDot = cleaned.indexOf(".");
    if (firstDot !== -1) {
      cleaned =
        cleaned.slice(0, firstDot + 1) +
        cleaned.slice(firstDot + 1).replace(/\./g, "");
    }
    setRaw(cleaned);
    if (cleaned !== "" && cleaned !== ".") {
      const num = Number(cleaned);
      if (!isNaN(num)) onChange(num);
    }
  };

  const handleBlur = () => {
    const num = Number(raw);
    if (raw === "" || isNaN(num)) {
      const fallback = min ?? 0;
      setRaw(String(fallback));
      onChange(fallback);
    } else if (min !== undefined && num < min) {
      setRaw(String(min));
      onChange(min);
    } else if (max !== undefined && num > max) {
      setRaw(String(max));
      onChange(max);
    }
  };

  return (
    <div>
      <label className="block text-sm font-medium text-gray-700 mb-1">
        {label}
      </label>
      <input
        type="text"
        inputMode="decimal"
        pattern="[0-9]*\.?[0-9]*"
        value={raw}
        min={min}
        max={max}
        step={step}
        disabled={disabled}
        onChange={handleChange}
        onBlur={handleBlur}
        onFocus={(e) => e.target.select()}
        onWheel={(e) => e.currentTarget.blur()}
        className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm disabled:bg-gray-100 disabled:cursor-not-allowed"
      />
      {hint && <p className="text-xs text-gray-500 mt-1">{hint}</p>}
    </div>
  );
}

function ToggleField({
  label,
  hint,
  checked,
  onChange,
  disabled,
}: {
  label: string;
  hint?: string;
  checked: boolean;
  onChange: (v: boolean) => void;
  disabled?: boolean;
}) {
  return (
    <div className="flex items-start justify-between gap-3">
      <div>
        <p className="text-sm font-medium text-gray-700">{label}</p>
        {hint && <p className="text-xs text-gray-500 mt-0.5">{hint}</p>}
      </div>
      <button
        type="button"
        onClick={() => onChange(!checked)}
        disabled={disabled}
        className={`relative inline-flex h-6 w-11 flex-shrink-0 items-center rounded-full transition-colors disabled:opacity-50 disabled:cursor-not-allowed ${
          checked ? "bg-indigo-600" : "bg-gray-300"
        }`}
      >
        <span
          className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
            checked ? "translate-x-6" : "translate-x-1"
          }`}
        />
      </button>
    </div>
  );
}

type OverrideMode = "closed" | "custom" | "block";

function OverrideModal({
  dateStr,
  existing,
  weeklySchedule,
  canManage,
  onClose,
  onSave,
  onDelete,
}: {
  dateStr: string;
  existing?: Override;
  weeklySchedule: WeeklySchedule;
  /** `true` si el usuario puede guardar/eliminar excepciones (permiso `servicios:editar`). */
  canManage: boolean;
  onClose: () => void;
  onSave: (data: {
    isClosed: boolean;
    reason: string;
    customShifts: Shift[] | null;
  }) => void;
  onDelete?: () => void;
}) {
  const { showToast } = useGlobalToast();
  const [mode, setMode] = useState<OverrideMode>(
    existing?.isClosed === false ? "custom" : "closed",
  );
  const [reason, setReason] = useState(existing?.reason ?? "");
  const [shifts, setShifts] = useState<Shift[]>(
    existing?.customShifts ?? [{ startTime: "10:00", endTime: "14:00" }],
  );
  const [blockedRanges, setBlockedRanges] = useState<Shift[]>([
    emptyBlockedRange(),
  ]);

  const dateLabel = parseLocalDate(dateStr).toLocaleDateString("es-ES", {
    weekday: "long",
    day: "numeric",
    month: "long",
    year: "numeric",
  });

  // Turnos "base" del día para el modo Bloquear: si ya hay un override con
  // customShifts se parte de esos; si no, del horario semanal del centro
  // para ese día de la semana (Date.getDay(): 0=domingo … 6=sábado).
  const weekdayKey = String(parseLocalDate(dateStr).getDay());
  const baseShiftsForDay: Shift[] =
    existing?.customShifts && existing.customShifts.length > 0
      ? existing.customShifts
      : weeklySchedule[weekdayKey]?.isOpen
        ? weeklySchedule[weekdayKey].shifts
        : [];
  const dayHasNoBase = baseShiftsForDay.length === 0;

  const previewShifts = subtractIntervals(baseShiftsForDay, blockedRanges);

  const handleSave = () => {
    if (mode === "closed") {
      onSave({ isClosed: true, reason: reason.trim(), customShifts: null });
      return;
    }
    if (mode === "custom") {
      if (shifts.length === 0) {
        showToast(
          "Añade al menos un turno para el horario especial.",
          "error",
        );
        return;
      }
      if (shifts.some((s) => s.startTime >= s.endTime)) {
        showToast(
          "Revisa los turnos: la hora de fin debe ser posterior a la de inicio.",
          "error",
        );
        return;
      }
      onSave({ isClosed: false, reason: reason.trim(), customShifts: shifts });
      return;
    }
    // mode === "block"
    if (dayHasNoBase) {
      showToast(
        "Ese día ya está cerrado (sin turnos base); no hay nada que bloquear.",
        "error",
      );
      return;
    }
    if (blockedRanges.some((b) => b.startTime >= b.endTime)) {
      showToast(
        "Revisa las franjas a bloquear: la hora de fin debe ser posterior a la de inicio.",
        "error",
      );
      return;
    }
    onSave({
      isClosed: false,
      reason: reason.trim(),
      customShifts: previewShifts,
    });
  };

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-end md:items-center justify-center p-0 md:p-4">
      <div className="bg-white w-full md:max-w-lg md:rounded-xl rounded-t-2xl shadow-xl max-h-[92dvh] overflow-y-auto">
        <div className="px-5 py-4 border-b flex items-center justify-between">
          <div>
            <h3 className="font-semibold text-gray-900 capitalize">
              {dateLabel}
            </h3>
            <p className="text-xs text-gray-500">Excepción de horario</p>
          </div>
          <button onClick={onClose}>
            <X className="w-5 h-5 text-gray-400 hover:text-gray-600" />
          </button>
        </div>
        <div className="p-5 space-y-4">
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
            <button
              onClick={() => setMode("closed")}
              disabled={!canManage}
              className={`py-2 px-3 rounded-lg border text-sm font-medium disabled:opacity-50 disabled:cursor-not-allowed ${
                mode === "closed"
                  ? "bg-red-50 border-red-300 text-red-700"
                  : "border-gray-200 text-gray-600"
              }`}
            >
              Cerrar este día
            </button>
            <button
              onClick={() => setMode("custom")}
              disabled={!canManage}
              className={`py-2 px-3 rounded-lg border text-sm font-medium disabled:opacity-50 disabled:cursor-not-allowed ${
                mode === "custom"
                  ? "bg-amber-50 border-amber-300 text-amber-700"
                  : "border-gray-200 text-gray-600"
              }`}
            >
              Horario especial
            </button>
            <button
              onClick={() => setMode("block")}
              disabled={!canManage}
              className={`py-2 px-3 rounded-lg border text-sm font-medium disabled:opacity-50 disabled:cursor-not-allowed ${
                mode === "block"
                  ? "bg-orange-50 border-orange-300 text-orange-700"
                  : "border-gray-200 text-gray-600"
              }`}
            >
              Bloquear una franja
            </button>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Motivo (opcional)
            </label>
            <input
              type="text"
              value={reason}
              disabled={!canManage}
              onChange={(e) => setReason(e.target.value)}
              placeholder="Festivo, vacaciones, formación…"
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm disabled:bg-gray-100 disabled:cursor-not-allowed"
            />
          </div>

          {mode === "custom" && (
            <div>
              <div className="flex items-center justify-between mb-2">
                <label className="block text-sm font-medium text-gray-700">
                  Turnos para este día
                </label>
                <button
                  onClick={() =>
                    setShifts([
                      ...shifts,
                      { startTime: "16:00", endTime: "20:00" },
                    ])
                  }
                  disabled={!canManage}
                  className="text-indigo-600 text-sm inline-flex items-center gap-1 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <Plus className="w-4 h-4" /> Turno
                </button>
              </div>
              <div className="space-y-2">
                {shifts.map((s, idx) => (
                  <div key={idx} className="flex items-center gap-2">
                    <input
                      type="time"
                      value={s.startTime}
                      disabled={!canManage}
                      onChange={(e) =>
                        setShifts(
                          shifts.map((sh, i) =>
                            i === idx
                              ? { ...sh, startTime: e.target.value }
                              : sh,
                          ),
                        )
                      }
                      className="px-2 py-1 border border-gray-300 rounded text-sm disabled:bg-gray-100 disabled:cursor-not-allowed"
                    />
                    <span className="text-gray-400">–</span>
                    <input
                      type="time"
                      value={s.endTime}
                      disabled={!canManage}
                      onChange={(e) =>
                        setShifts(
                          shifts.map((sh, i) =>
                            i === idx ? { ...sh, endTime: e.target.value } : sh,
                          ),
                        )
                      }
                      className="px-2 py-1 border border-gray-300 rounded text-sm disabled:bg-gray-100 disabled:cursor-not-allowed"
                    />
                    <button
                      onClick={() =>
                        setShifts(shifts.filter((_, i) => i !== idx))
                      }
                      disabled={!canManage}
                      className="ml-auto text-red-500 hover:text-red-700 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                ))}
              </div>
            </div>
          )}

          {mode === "block" && (
            <div className="space-y-3">
              <div className="rounded-lg bg-gray-50 border border-gray-200 p-3 text-xs text-gray-600">
                <p className="font-medium text-gray-700 mb-1">
                  Horario base de ese día
                </p>
                {dayHasNoBase ? (
                  <p className="text-amber-600">
                    Este día ya está cerrado (sin turnos); no hay nada que
                    bloquear.
                  </p>
                ) : (
                  <p>
                    {baseShiftsForDay
                      .map((s) => `${s.startTime}–${s.endTime}`)
                      .join(", ")}
                  </p>
                )}
              </div>

              <div>
                <div className="flex items-center justify-between mb-2">
                  <label className="block text-sm font-medium text-gray-700">
                    Franjas a bloquear
                  </label>
                  <button
                    onClick={() =>
                      setBlockedRanges([...blockedRanges, emptyBlockedRange()])
                    }
                    disabled={dayHasNoBase || !canManage}
                    className="text-indigo-600 text-sm inline-flex items-center gap-1 disabled:opacity-40 disabled:cursor-not-allowed"
                  >
                    <Plus className="w-4 h-4" /> Franja
                  </button>
                </div>
                <div className="space-y-2">
                  {blockedRanges.map((s, idx) => (
                    <div key={idx} className="flex items-center gap-2">
                      <input
                        type="time"
                        value={s.startTime}
                        disabled={dayHasNoBase || !canManage}
                        onChange={(e) =>
                          setBlockedRanges(
                            blockedRanges.map((sh, i) =>
                              i === idx
                                ? { ...sh, startTime: e.target.value }
                                : sh,
                            ),
                          )
                        }
                        className="px-2 py-1 border border-gray-300 rounded text-sm disabled:bg-gray-100"
                      />
                      <span className="text-gray-400">–</span>
                      <input
                        type="time"
                        value={s.endTime}
                        disabled={dayHasNoBase || !canManage}
                        onChange={(e) =>
                          setBlockedRanges(
                            blockedRanges.map((sh, i) =>
                              i === idx
                                ? { ...sh, endTime: e.target.value }
                                : sh,
                            ),
                          )
                        }
                        className="px-2 py-1 border border-gray-300 rounded text-sm disabled:bg-gray-100"
                      />
                      <button
                        onClick={() =>
                          setBlockedRanges(
                            blockedRanges.filter((_, i) => i !== idx),
                          )
                        }
                        disabled={
                          dayHasNoBase || blockedRanges.length <= 1 || !canManage
                        }
                        className="ml-auto text-red-500 hover:text-red-700 disabled:opacity-40 disabled:cursor-not-allowed"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  ))}
                </div>
              </div>

              {!dayHasNoBase && (
                <div
                  className={`rounded-lg border p-3 text-xs ${
                    previewShifts.length > 0
                      ? "bg-emerald-50 border-emerald-200 text-emerald-700"
                      : "bg-red-50 border-red-200 text-red-700"
                  }`}
                >
                  <p className="font-medium mb-1">Así quedará el día</p>
                  <p>
                    {previewShifts.length > 0
                      ? `Abierto: ${previewShifts
                          .map((s) => `${s.startTime}-${s.endTime}`)
                          .join(", ")}`
                      : "Cerrado todo el día (los bloqueos cubren todo el horario)."}
                  </p>
                </div>
              )}
            </div>
          )}
        </div>
        <div className="px-5 py-3 border-t flex items-center justify-between gap-2">
          {onDelete ? (
            <button
              onClick={onDelete}
              disabled={!canManage}
              className="text-sm text-red-600 hover:text-red-700 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Eliminar excepción
            </button>
          ) : (
            <span />
          )}
          <div className="flex gap-2">
            <button
              onClick={onClose}
              className="px-3 py-2 text-sm text-gray-600 hover:bg-gray-100 rounded-lg"
            >
              Cancelar
            </button>
            <button
              onClick={handleSave}
              disabled={(mode === "block" && dayHasNoBase) || !canManage}
              className="px-4 py-2 text-sm bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Guardar
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
